import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SvgComponent } from '@shared/components/svg';
import { LogsService } from '@shared/services/logs';
import { ResponsiveService } from '@shared/services/responsive';

@Component({
  selector: 'app-youtube-player',
  imports: [SvgComponent],
  templateUrl: './youtube-player.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class YoutubePlayerComponent {
  videoId = input.required<string>();
  playRequested = output<void>();
  showVideoPlayer = signal(false);
  private readonly useMaxRes = signal(true);
  readonly videoImgUrl = computed<string>(() => {
    const quality = this.isMobile() || !this.useMaxRes() ? 'hqdefault' : 'maxresdefault';
    return `https://img.youtube.com/vi/${this.videoId()}/${quality}.jpg`;
  });
  private readonly sanitizer = inject(DomSanitizer);
  videoUrl = computed<SafeResourceUrl>(() => {
    const url = `https://www.youtube-nocookie.com/embed/${this.videoId()}?autoplay=1&rel=0`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });
  private readonly logs = inject(LogsService);
  private readonly breakpoint = inject(ResponsiveService).breakpoints;
  readonly isMobile = computed(() => this.breakpoint() === 'mobile');

  onPlaceholderClick() {
    this.playRequested.emit();
    this.showVideoPlayer.set(true);
  }

  onThumbnailLoad(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    if (imgElement.naturalHeight === 90 && !this.isMobile() && this.useMaxRes()) {
      this.logs.warn(
        'YoutubePlayerComponent',
        `'maxresdefault.jpg' not found for video ${this.videoId()}. Falling back to 'hqdefault.jpg'.`
      );
      this.useMaxRes.set(false);
    }
  }
}
